import { useEffect, useState } from "react";
import { Badge, Btn, Card, Dot, I, Toggle } from "../ui";
import type { AdapterInfoDTO, SourceDTO } from "../../api/contract";
import { SOURCE_SCHEDULE_OPTIONS, sourceScheduleLabel } from "../../api/source-schedule";
import {
  ApiRequestError,
  createSource,
  deleteSourceApi,
  listAdapters,
  listSources,
  runSource,
  seedDefaultSources,
  updateSource,
} from "../api";

export function Sources({ pushToast, isAdmin }: { pushToast: (kind: "ok" | "err", msg: string) => void; isAdmin: boolean }) {
  const [sources, setSources] = useState<SourceDTO[]>([]);
  const [adapters, setAdapters] = useState<AdapterInfoDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newAdapter, setNewAdapter] = useState("");
  const [newName, setNewName] = useState("");
  const [newProv, setNewProv] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newCronValue, setNewCronValue] = useState(DAILY_SCHEDULE_VALUE);

  const reload = async () => {
    try {
      setLoading(true);
      const [s, a] = await Promise.all([listSources(), isAdmin ? listAdapters() : Promise.resolve([])]);
      setSources(s);
      setAdapters(a);
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [isAdmin]);

  const handleSeed = async () => {
    try {
      const { added } = await seedDefaultSources();
      pushToast("ok", added > 0 ? `${added} source aggiunte` : "Già seedate");
      await reload();
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore seed");
    }
  };

  const handleToggleActive = async (s: SourceDTO) => {
    try {
      const updated = await updateSource(s.id, { active: !s.active });
      setSources((prev) => prev.map((p) => (p.id === s.id ? updated : p)));
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore update");
    }
  };

  const handleScheduleChange = async (s: SourceDTO, value: string) => {
    try {
      setBusyId(s.id);
      const updated = await updateSource(s.id, { cron: cronFromSelectValue(value) });
      setSources((prev) => prev.map((p) => (p.id === s.id ? updated : p)));
      pushToast("ok", `Schedulazione aggiornata · ${updated.name}`);
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore schedule");
    } finally {
      setBusyId(null);
    }
  };

  const handleRun = async (s: SourceDTO, opts: { test: boolean }) => {
    try {
      setBusyId(s.id);
      const limit = opts.test ? 10 : 50;
      const res = await runSource(s.id, { limit, dryRun: opts.test });
      pushToast("ok", opts.test ? `${res.items} annunci verificati in ${(res.elapsedMs / 1000).toFixed(1)}s${res.warnings.length ? ` · ${res.warnings.length} avvisi` : ""}` : `${res.inserted} nuovi, ${res.updated} aggiornati su ${res.items} in ${(res.elapsedMs / 1000).toFixed(1)}s`);
      if (res.warnings.length) {
        console.warn(`[sources] warnings:`, res.warnings);
      }
      await reload();
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore scrape");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (s: SourceDTO) => {
    if (!confirm(`Eliminare la source "${s.name}"? (i necrologi raccolti non verranno cancellati)`)) {
      return;
    }
    try {
      await deleteSourceApi(s.id);
      setSources((prev) => prev.filter((p) => p.id !== s.id));
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore delete");
    }
  };

  const handleAdd = async () => {
    if (!newAdapter || !newName || !newUrl) {
      pushToast("err", "Compila adapter, nome, baseUrl");
      return;
    }
    try {
      const dto = await createSource({
        adapter: newAdapter,
        name: newName,
        provincia: newProv || null,
        baseUrl: newUrl,
        active: true,
        cron: cronFromSelectValue(newCronValue),
      });
      setSources((prev) => [...prev, dto]);
      setShowAdd(false);
      setNewAdapter("");
      setNewName("");
      setNewProv("");
      setNewUrl("");
      setNewCronValue(DAILY_SCHEDULE_VALUE);
      pushToast("ok", `Source "${dto.name}" aggiunta`);
    } catch (err) {
      pushToast("err", err instanceof ApiRequestError ? err.message : "Errore create");
    }
  };

  const onPickAdapter = (id: string) => {
    setNewAdapter(id);
    const a = adapters.find((x) => x.id === id);
    if (a) {
      if (!newName) setNewName(a.name);
      if (!newProv) setNewProv(a.provincia);
      if (!newUrl) setNewUrl(a.baseUrl);
    }
  };

  return (
    <div className="col gap-16">
      <Card
        title="Sources registry"
        sub="Ogni fonte è un portale remoto da cui raccogliere necrologi"
        actions={
          <div className="row gap-6">
            <Btn size="sm" variant="ghost" icon={I.refresh} onClick={() => void reload()}>
              Refresh
            </Btn>
            {isAdmin && <Btn size="sm" variant="ghost" onClick={() => void handleSeed()}>
              Seed defaults
            </Btn>}
            {isAdmin && <Btn size="sm" variant="primary" icon={I.plus} onClick={() => setShowAdd(!showAdd)}>
              {showAdd ? "Annulla" : "Aggiungi"}
            </Btn>}
          </div>
        }
      >
        {isAdmin && showAdd && (
          <div
            className="preview-box"
            style={{ marginBottom: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div className="field" style={{ minWidth: 200 }}>
                <label className="label">Adapter</label>
                <select
                  className="select"
                  value={newAdapter}
                  onChange={(e) => onPickAdapter(e.target.value)}
                >
                  <option value="">Seleziona…</option>
                  {adapters.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ minWidth: 200 }}>
                <label className="label">Nome</label>
                <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="field" style={{ width: 100 }}>
                <label className="label">Provincia</label>
                <input
                  className="input mono"
                  value={newProv}
                  maxLength={2}
                  onChange={(e) => setNewProv(e.target.value.toUpperCase())}
                />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 260 }}>
                <label className="label">Base URL</label>
                <input
                  className="input mono"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                />
              </div>
              <div className="field" style={{ minWidth: 160 }}>
                <label className="label">Schedulazione</label>
                <select
                  className="select"
                  value={newCronValue}
                  onChange={(e) => setNewCronValue(e.target.value)}
                >
                  {SOURCE_SCHEDULE_OPTIONS.map((option) => (
                    <option key={option.label} value={selectValueForCron(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
              <Btn size="sm" variant="primary" onClick={() => void handleAdd()}>
                Crea
              </Btn>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
        ) : sources.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-2)", fontSize: 13 }}>
            {isAdmin ? 'Nessuna source configurata. Clicca "Seed defaults" per creare quelle predefinite o "Aggiungi".' : "Nessuna fonte configurata."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Attiva</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Nome</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Adapter</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Prov.</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Sched.</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Ultimo run</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Esito</th>
                  {isAdmin && <th style={{ textAlign: "right", padding: "8px 12px" }}>Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.id} style={{ borderTop: "1px solid var(--line)" }}>
                    <td style={{ padding: "8px 12px" }}>
                      {isAdmin ? <Toggle on={s.active} onChange={() => void handleToggleActive(s)} /> : <Badge kind={s.active ? "accent" : "muted"}>{s.active ? "Attiva" : "Disattiva"}</Badge>}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <div style={{ fontWeight: 500 }}>{s.name}</div>
                      <div className="mono" style={{ fontSize: 10, color: "var(--text-3)" }}>
                        {s.baseUrl}
                      </div>
                    </td>
                    <td className="mono" style={{ padding: "8px 12px" }}>{s.adapter}</td>
                    <td className="mono" style={{ padding: "8px 12px" }}>{s.provincia ?? "—"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {isAdmin && <select
                        className="select"
                        value={selectValueForCron(s.cron)}
                        disabled={busyId === s.id}
                        onChange={(e) => void handleScheduleChange(s, e.target.value)}
                      >
                        {SOURCE_SCHEDULE_OPTIONS.map((option) => (
                          <option key={option.label} value={selectValueForCron(option.value)}>
                            {option.label}
                          </option>
                        ))}
                      </select>}
                      <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>{sourceScheduleLabel(s.cron)}</div>
                    </td>
                    <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                      {s.lastRunAt ? formatRelative(s.lastRunAt) : "mai"}
                      {s.lastRunCount != null && (
                        <span className="muted" style={{ marginLeft: 6 }}>· {s.lastRunCount} nuovi</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {s.lastRunStatus === "ok" ? (
                        <div className="row gap-6" style={{ alignItems: "center" }}>
                          <Dot kind="live" />
                          <Badge kind="accent">ok</Badge>
                        </div>
                      ) : s.lastRunStatus === "warn" ? (
                        <div className="row gap-6" style={{ alignItems: "center" }} title={s.lastRunError ?? ""}>
                          <Badge kind="muted">avvisi</Badge>
                        </div>
                      ) : s.lastRunStatus === "err" ? (
                        <div className="row gap-6" style={{ alignItems: "center" }} title={s.lastRunError ?? ""}>
                          <Dot kind="danger" />
                          <Badge kind="muted">err</Badge>
                        </div>
                      ) : (
                        <Badge kind="muted">—</Badge>
                      )}
                    </td>
                    {isAdmin && <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <Btn
                        size="sm"
                        variant="ghost"
                        disabled={busyId === s.id || !s.active}
                        onClick={() => void handleRun(s, { test: true })}
                      >
                        {busyId === s.id ? "…" : "Test (10)"}
                      </Btn>
                      <span style={{ display: "inline-block", width: 4 }} />
                      <Btn
                        size="sm"
                        variant="primary"
                        disabled={busyId === s.id || !s.active}
                        onClick={() => void handleRun(s, { test: false })}
                      >
                        {busyId === s.id ? "…" : "Run (50)"}
                      </Btn>
                      <span style={{ display: "inline-block", width: 4 }} />
                      <Btn size="sm" variant="ghost" onClick={() => void handleDelete(s)}>
                        ×
                      </Btn>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {isAdmin && <Card title="Adapter disponibili" sub="codice lato server · un adapter per portale">
        {adapters.length === 0 ? (
          <div style={{ color: "var(--text-2)", fontSize: 13 }}>Nessun adapter registrato.</div>
        ) : (
          <div className="col gap-8">
            {adapters.map((a) => (
              <div
                key={a.id}
                className="preview-box"
                style={{ padding: 10, fontSize: 12 }}
              >
                <div className="row gap-6" style={{ alignItems: "center" }}>
                  <strong>{a.name}</strong>
                  <Badge kind="muted" className="mono">{a.id}</Badge>
                  <Badge kind="accent">{a.provincia}</Badge>
                  <span className="grow" />
                </div>
                <div className="muted" style={{ marginTop: 4 }}>{a.description}</div>
                <div className="mono" style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2 }}>{a.baseUrl}</div>
              </div>
            ))}
          </div>
        )}
      </Card>}
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const delta = Date.now() - d.getTime();
  const min = Math.round(delta / 60_000);
  if (min < 60) return `${min}m fa`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h fa`;
  const day = Math.round(h / 24);
  return `${day}g fa`;
}

const DAILY_SCHEDULE_VALUE = "__daily__";

function selectValueForCron(cron: string | null): string {
  return cron ?? DAILY_SCHEDULE_VALUE;
}

function cronFromSelectValue(value: string): string | null {
  return value === DAILY_SCHEDULE_VALUE ? null : value;
}
